import React, { useCallback } from 'react';
import { prop } from 'ramda';
import { useTranslations } from 'js/hooks/i18n';
import { useUserSelector } from 'js/store/hooks';
import { AccessibilityElement } from 'js/components/common/AccessibilityElement';
import { useSnackbar, type SnackbarMessage, type OptionsWithExtraProps, type SnackbarKey, type VariantType } from 'notistack';

import style from './Notifications.scss';

const DEFAULT_OPTIONS = {
    hideIconVariant: true,
    disableWindowBlurListener: true,
    SnackbarProps: {
        'aria-live': 'polite'
    } as React.HTMLAttributes<HTMLDivElement>
};

export const useShowNotification = (options?: OptionsWithExtraProps<VariantType>) => {
    const translate = useTranslations();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const toastTimeoutValue = useUserSelector(prop('toastTimeoutValue'));

    return useCallback((
        messageOrTKey: SnackbarMessage,
        toastOptions?: OptionsWithExtraProps<VariantType>
    ): SnackbarKey => {
        const snackbarOptions: OptionsWithExtraProps<VariantType> = {
            ...DEFAULT_OPTIONS,
            autoHideDuration: toastTimeoutValue,
            ...window.Playtech.API.common.notificationsOptions || {}
        };

        if (options) {
            Object.assign(snackbarOptions, options);
        }

        if (toastOptions) {
            Object.assign(snackbarOptions, toastOptions);
        }

        snackbarOptions.className = `${style.notification} ${style[snackbarOptions.variant || 'default']} ${snackbarOptions.className ?? ''}`;
        snackbarOptions.action = (snackbarId: SnackbarKey) => (
            <AccessibilityElement onClick={() => closeSnackbar(snackbarId)} className={style.closeButton}>
                <span className={style.text}>{translate('playtech.mobile.system.notification.close')}</span>
            </AccessibilityElement>
        );

        const content = typeof messageOrTKey === 'string' && messageOrTKey.startsWith('playtech.mobile.')
            ? translate(messageOrTKey)
            : messageOrTKey;

        const message = typeof content === 'string'
            ? <span dangerouslySetInnerHTML={{ __html: content }} />
            : content;
        const snackbarOptionsParsed = {
            ...snackbarOptions,
            ...snackbarOptions.autoHideDuration && { autoHideDuration: snackbarOptions.autoHideDuration * 1000 }
        };

        return enqueueSnackbar(message, snackbarOptionsParsed);
    }, [options, translate, enqueueSnackbar, closeSnackbar, toastTimeoutValue]);
};
